import React, { useState, useMemo, useEffect } from "react";
import { Search, Plus, ChevronRight, Building2, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import useApi from "../../utils/useApi";

// ---------------------------------------------------------------------------
// API data — fetched from CRMAccountListPage
// ---------------------------------------------------------------------------

const initials = (name) =>
    name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase();

const AVATAR_TONES = [
    "bg-blue-50 text-blue-600",
    "bg-violet-50 text-violet-600",
    "bg-emerald-50 text-emerald-600",
    "bg-amber-50 text-amber-600",
    "bg-rose-50 text-rose-600",
    "bg-cyan-50 text-cyan-600",
];
const toneFor = (i) => AVATAR_TONES[i % AVATAR_TONES.length];

const money = (n) =>
    new Intl.NumberFormat("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

// ---------------------------------------------------------------------------
// LIST VIEW
// ---------------------------------------------------------------------------
function TenantListView({ tenants, loading, onNew, onEdit }) {
    const [query, setQuery] = useState("");

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return tenants;
        return tenants.filter(
            (t) =>
                t.name.toLowerCase().includes(q) ||
                t.account.toLowerCase().includes(q) ||
                (t.address2 || "").toLowerCase().includes(q)
        );
    }, [tenants, query]);

    return (
        <div className="mx-auto ">
            {/* Header */}
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                    {/* <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-sm shadow-blue-600/30">
            <Building2 size={20} />
          </span> */}
                    <div>
                        {/* <h1 className="text-xl font-semibold tracking-tight text-slate-900">Tenant Register</h1> */}
                        <h2 className="text-2xl font-normal text-slate-900 tracking-tight">Tenant Register</h2>
                        <p className="text-xs text-slate-500">{tenants.length} tenants on file</p>
                    </div>
                </div>
                <button
                    onClick={onNew}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm shadow-blue-600/20 transition hover:bg-blue-700 active:scale-[0.98]"
                >
                    <Plus size={16} strokeWidth={2.5} />
                    New tenant
                </button>
            </div>

            {/* Search */}
            <div className="relative mb-4">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={17} />
                <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search by name, account, or address"
                    className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-800 shadow-sm placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10"
                />
            </div>

            {/* Table card */}
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[760px] border-collapse text-sm">
                        <thead>
                            <tr className="border-b border-slate-100 bg-slate-50/80 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                                <th className="px-5 py-3">Tenant</th>
                                <th className="px-5 py-3">Account</th>
                                <th className="px-5 py-3">Address</th>
                                <th className="px-5 py-3 text-right">Opening bal.</th>
                                <th className="px-5 py-3 text-right">Balance</th>
                                <th className="px-5 py-3"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {loading ? (
                                <tr>
                                    <td colSpan={6} className="px-5 py-14 text-center">
                                        <Loader2 size={24} className="animate-spin text-blue-500 mx-auto" />
                                        <p className="mt-2 text-sm text-slate-500">Loading tenants...</p>
                                    </td>
                                </tr>
                            ) : filtered.map((t, i) => (
                                <tr
                                    key={t.account}
                                    onClick={() => onEdit(t)}
                                    className="group cursor-pointer transition hover:bg-blue-50/40"
                                >
                                    <td className="px-5 py-3.5">
                                        <div className="flex items-center gap-3">
                                            <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${toneFor(i)}`}>
                                                {initials(t.name)}
                                            </span>
                                            <span className="font-medium text-slate-800">{t.name}</span>
                                        </div>
                                    </td>
                                    <td className="px-5 py-3.5 font-mono text-xs text-slate-500">{t.account}</td>
                                    <td className="px-5 py-3.5 text-slate-500">
                                        {[t.address1, t.address2].filter(Boolean).join(", ") || (
                                            <span className="text-slate-300">—</span>
                                        )}
                                    </td>
                                    <td className="px-5 py-3.5 text-right tabular-nums text-slate-600">{money(t.opBal)}</td>
                                    <td className="px-5 py-3.5 text-right">
                                        <span
                                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium tabular-nums ${t.balance > 0
                                                ? "bg-amber-50 text-amber-700"
                                                : "bg-slate-50 text-slate-500"
                                                }`}
                                        >
                                            {money(t.balance)}
                                        </span>
                                    </td>
                                    <td className="px-5 py-3.5 text-right">
                                        <ChevronRight size={16} className="ml-auto text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-blue-500" />
                                    </td>
                                </tr>
                            ))}

                            {!loading && filtered.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="px-5 py-14 text-center">
                                        <p className="text-sm font-medium text-slate-600">No tenants match "{query}"</p>
                                        <p className="mt-1 text-xs text-slate-400">Try a different name, account number, or address.</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}



// ---------------------------------------------------------------------------
// ROOT
// ---------------------------------------------------------------------------
export default function TenantRegisterApp() {
    const [tenants, setTenants] = useState([]);
    const navigate = useNavigate();
    const { data: apiData, loading, get: fetchTenants } = useApi();

    useEffect(() => {
        const baseUrl = localStorage.getItem("apiPaths")
            ? JSON.parse(localStorage.getItem("apiPaths"))[0].API_CONFIG
            : "";
        const companyCode = localStorage.getItem("companyCode") || "";
        let department = localStorage.getItem("currentSelectedDepartment") || "-";
        fetchTenants(`${baseUrl}/api/CRMAccountListPage/${companyCode}/ACCOUNTLIST/${department}/CUSTOMERS/-/-/1/50`);
    }, []);

    useEffect(() => {
        if (apiData && Array.isArray(apiData)) {
            const mapped = apiData.map((item) => ({
                account: item.ACCOUNT || "",
                name: item.DESCRIPTION || item.Patron_Type || item.ACCOUNT || "",
                opBal: item.openbal || 0,
                balance: item.BALANCE || 0,
                address1: "",
                address2: "",
                tel: item.Contact_No || "",
                creditLimit: item.Credit_Limit || 0,
                creditMode: item.Credit_Mode || "",
                dueDays: item.Due_Days || 0,
                salesPerson: item.Sales_Person || "",
            }));
            setTenants(mapped);
        }
    }, [apiData]);

    const goNew = () => {
        navigate("/tenantCreation");
    };
    const goEdit = (t) => {
        navigate("/tenantCreation", {
            state: { editAccNo: t.account, tenantName: t.name },
        });
    };

    return (
        <div className="min-h-screen bg-slate-50 px-4 py-8 sm:px-8">
            <TenantListView tenants={tenants} loading={loading} onNew={goNew} onEdit={goEdit} />
        </div>
    );
}